/** R3 "Record a meeting": client, where, how Preekie hears the call, the
 * sound check (with the echo test and the headphones prompt), consent, and
 * Start (decision 0051 v1; docs/recorder-api.md). The logic is in
 * recorder.ts; this file is the DOM and the browser's media calls. */
import { ApiError, currentCsrfToken, startRecorder } from "./api.js";
import { httpRecorderApi } from "./recorder/api.js";
import { loadUnsent } from "./recorder-banner.js";
import { confirmDialog } from "./recorder-live.js";
import type { RecorderStarted } from "./api.js";
import { pickMimeType, levelFromBytes } from "./device-check-core.js";
import type { LevelSample } from "./device-check-core.js";
import {
  ENTIRE_SCREEN_NOTICE,
  RECORDER_COPY as T,
  displayMediaOptions,
  echoResult,
  heard,
  keepAudioOnly,
  micConstraints,
  NOT_YET_AVAILABLE,
  limitLine,
  openElsewhereCopy,
  DISCARD_FAILED,
  isSignedOut,
  ROUTE_CHANGED,
  SIGNED_OUT,
  sessionState,
  meterValue,
  recorderMediaError,
  shareCancelledMessage,
  routeForSurface,
  startBlockers,
  startBody,
  clientInitials,
  consentFor,
} from "./recorder.js";
import type { CallSource, RecordMode, StartState } from "./recorder.js";
import { el } from "./render.js";

export interface RecorderStartOptions {
  root: HTMLElement;
  clients: ReadonlyArray<{ client_slug: string; client_name: string }>;
  clientSlug: string | null;
  /** False until the recording screen ships: Start stays disabled and no
   * recording is created on the server. */
  recordingAvailable: boolean;
  timeZone: string;
  /** /api/me's recording_limits.limit_minutes (CAP-008.WP11), or null when the server sends none. */
  recordingLimitMinutes: number | null;
  /** Start succeeded: the consent record exists and the token is issued. The
   * live microphone and call streams are handed over to the recording screen. */
  onStarted: (started: RecorderStarted, streams: { mic: MediaStream; call: MediaStream | null }, state: StartState) => void;
  /** CAP-008 #303: checks the console session before anything else (a call that rejects with a 401 when signed out). */
  checkSession?: () => Promise<unknown>;
  /** Sign in, then come back to Record a meeting with this client chosen. */
  onSignIn?: (clientSlug: string | null) => void;
  /** CAP-010 (the new console): the client as selectable cards, then "Recording for <client>" with Change, instead of the select. */
  clientCards?: boolean;
  /** Called when the chosen client changes (the new console puts it in the breadcrumb). */
  onClientChange?: (clientSlug: string) => void;
  /** CAP-009: true only where the live screen will offer Pause; the start screen then shows and sends the pause wording (2026-10-24). */
  pauseOffered?: boolean;
}

export interface Mounted {
  destroy(): void;
}

const ECHO_SECONDS = 5;
const CHECKING_SESSION = "Checking you're still signed in…";

function button(text: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", cls, text);
  b.type = "button";
  b.addEventListener("click", onClick);
  return b;
}

export function mountRecorderStart(opts: RecorderStartOptions): Mounted {
  const consentShown = consentFor(opts.pauseOffered === true);
  const state: StartState = {
    clientSlug: opts.clientSlug ?? (opts.clients.length === 1 ? opts.clients[0]!.client_slug : ""),
    mode: "online",
    source: "tab",
    mic: "not_run",
    call: "not_run",
    surface: null,
    echo: "not_run",
    headphones: false,
    consent: false,
  };
  let ctx: AudioContext | null = null;
  let mic: MediaStream | null = null;
  let call: MediaStream | null = null;
  let routeNote: string | null = null;
  const analysers: { mic: AnalyserNode | null; call: AnalyserNode | null } = { mic: null, call: null };
  const levels: { mic: number[]; call: number[] } = { mic: [], call: [] };
  const samples: LevelSample[] = [];
  let t0 = 0;
  let timer = 0;
  let echoUntil = 0;
  let error: string | null = null;
  let checking = false;
  let starting = false;
  let handedOver = false;
  let destroyed = false;
  // #303: checked first, before the sound check and consent; a 401 on any recorder action turns it to signed_out.
  let session: "checking" | "ok" | "signed_out" = opts.checkSession ? "checking" : "ok";

  /** The session has ended: release the microphone and the share, and show only the sign-in panel. */
  function signedOut(): void {
    session = "signed_out";
    window.clearInterval(timer);
    stopStream(mic);
    stopStream(call);
    mic = null;
    call = null;
    analysers.mic = null;
    analysers.call = null;
    void ctx?.close();
    ctx = null;
    draw();
  }

  function stopStream(s: MediaStream | null): void {
    s?.getTracks().forEach((t) => t.stop());
  }

  function analyse(stream: MediaStream): AnalyserNode {
    const source = ctx!.createMediaStreamSource(stream);
    const a = ctx!.createAnalyser();
    a.fftSize = 1024;
    source.connect(a); // never to the speakers: the check doesn't play anything back
    return a;
  }

  function read(a: AnalyserNode | null): number {
    if (!a) return 0;
    const buf = new Uint8Array(a.fftSize);
    a.getByteTimeDomainData(buf);
    return levelFromBytes(buf);
  }

  /** Every 100 ms: levels, the meters (updated in place, no redraw) and the
   * sticky "heard" verdicts. */
  function tick(): void {
    const m = read(analysers.mic);
    const c = read(analysers.call);
    levels.mic.push(m);
    levels.call.push(c);
    samples.push({ t: (performance.now() - t0) / 1000, mic: m, call: c });
    for (const k of ["mic", "call"] as const) {
      const bar = opts.root.querySelector<HTMLElement>(`[data-meter="${k}"] > div`);
      if (bar) {
        const pct = Math.round(meterValue(levels[k]) * 100);
        bar.style.width = `${pct}%`;
        bar.parentElement?.setAttribute("aria-valuenow", String(pct));
      }
    }
    let changed = false;
    if (state.mic !== "ok" && heard(levels.mic.slice(-30)) === "ok") (state.mic = "ok"), (changed = true);
    if (analysers.call && state.call !== "ok" && state.call !== "ended" && heard(levels.call.slice(-30)) === "ok")
      (state.call = "ok"), (changed = true);
    if (echoUntil && performance.now() >= echoUntil) {
      const now = (performance.now() - t0) / 1000;
      state.echo = echoResult(samples, now - ECHO_SECONDS, now);
      echoUntil = 0;
      changed = true;
    }
    if (changed) draw();
  }

  /** The call: a share whose video is dropped on arrival. From the click
   * itself (Chrome refuses a share once the click's activation has lapsed). */
  async function shareCall(): Promise<void> {
    stopStream(call);
    call = null;
    analysers.call = null;
    levels.call = [];
    const display = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions(state.source) as DisplayMediaStreamOptions);
    const shared = keepAudioOnly(display);
    state.surface = shared.surface;
    const route = routeForSurface(state.source, shared.surface);
    routeNote = route !== state.source ? ROUTE_CHANGED[route] : null;
    if (route !== state.source) {
      state.source = route;
      state.echo = "not_run";
      state.headphones = false;
    }
    if (shared.audioTracks === 0) {
      stopStream(display);
      state.call = "no_track";
      return;
    }
    call = new MediaStream(display.getAudioTracks());
    for (const track of call.getAudioTracks()) {
      // Stopping sharing from the browser's own bar ends the track: say so at once.
      track.addEventListener("ended", () => {
        if (destroyed || handedOver) return;
        state.call = "ended";
        analysers.call = null;
        draw();
      });
    }
    analysers.call = analyse(call);
    state.call = "silent";
  }

  async function checkSound(): Promise<void> {
    if (checking || session !== "ok") return;
    checking = true;
    error = null;
    draw();
    try {
      if (!ctx) {
        ctx = new AudioContext();
        void ctx.resume();
        t0 = performance.now();
        timer = window.setInterval(tick, 100);
      }
      if (state.mode === "online") await shareCall();
      if (!mic) {
        mic = await navigator.mediaDevices.getUserMedia({ audio: micConstraints(state.mode) });
        analysers.mic = analyse(mic);
        state.mic = "silent";
      }
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      error =
        name === "NotAllowedError" && state.mode === "online" && !call
          ? shareCancelledMessage(state.source)
          : recorderMediaError(err);
    }
    checking = false;
    draw();
  }

  async function shareAgain(): Promise<void> {
    error = null;
    try {
      await shareCall();
    } catch (err) {
      error = recorderMediaError(err);
    }
    draw();
  }

  function runEcho(): void {
    state.echo = "not_run";
    echoUntil = performance.now() + ECHO_SECONDS * 1000;
    draw();
  }

  /** Open recordings the server holds that this computer doesn't (the R8
   * banner already covers the ones here): shown with Discard, so a stuck
   * recording can't silently block the next Start. */
  let openElsewhere: Array<{ recording_id: string; started_at: string | null }> = [];
  async function loadOpenElsewhere(): Promise<void> {
    if (!opts.recordingAvailable) return;
    try {
      const api = httpRecorderApi(() => currentCsrfToken() ?? "");
      const [server, here] = await Promise.all([api.unsent(), loadUnsent(null)]);
      const local = new Set(here.map((r) => r.recording_id));
      openElsewhere = server.recordings
        .filter((r) => !local.has(r.recording_id))
        .map((r) => ({ recording_id: r.recording_id, started_at: (r as { started_at?: string }).started_at ?? null }));
    } catch (err) {
      openElsewhere = []; // switched off: nothing to show
      if (isSignedOut(err)) return signedOut();
    }
    draw();
  }

  function discardElsewhere(id: string, startedAt: string | null): void {
    const c = openElsewhereCopy(startedAt, opts.timeZone);
    confirmDialog({
      question: c.question,
      confirm: c.confirm,
      cancel: c.cancel,
      onConfirm: () => {
        void httpRecorderApi(() => currentCsrfToken() ?? "")
          .discard(id, true)
          .then(
            () => {
              error = null;
            },
            (err: unknown) => {
              if (isSignedOut(err)) return signedOut();
              // Already finished elsewhere (409) is fine; anything else is said, not swallowed (CAP-008 #266).
              error = (err as { status?: number } | null)?.status === 409 ? null : DISCARD_FAILED;
            }
          )
          .then(() => loadOpenElsewhere());
      },
    });
  }

  async function start(): Promise<void> {
    if (!opts.recordingAvailable || session !== "ok" || starting || startBlockers(state).length || !mic) return;
    starting = true;
    error = null;
    draw();
    const mime = pickMimeType((m) => typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m));
    if (!mime) {
      error = "This browser can't record audio. Use Chrome or Edge on a computer, or Safari on an iPhone.";
      starting = false;
      draw();
      return;
    }
    const client = opts.clients.find((c) => c.client_slug === state.clientSlug);
    try {
      const started = await startRecorder(
        startBody(state, { title: `Meeting with ${client?.client_name ?? "a client"}`, mimeType: mime, consentVersion: consentShown.version })
      );
      handedOver = true;
      window.clearInterval(timer);
      void ctx?.close();
      opts.onStarted(started, { mic, call: state.mode === "online" ? call : null }, { ...state });
      return;
    } catch (err) {
      if (isSignedOut(err)) {
        starting = false;
        return signedOut();
      }
      error =
        err instanceof ApiError && err.status === 404
          ? "Recording isn't switched on on this server yet."
          : err instanceof ApiError
            ? err.message
            : "Preekie couldn't start the recording. Check your connection and try again.";
      if (err instanceof ApiError && err.code === "recording_active") void loadOpenElsewhere();
    }
    starting = false;
    draw();
  }

  function radio(name: string, value: string, checked: boolean, label: string, hint: string, onPick: () => void): HTMLLabelElement {
    const l = el("label", `pk-choice${checked ? " pk-choice-on" : ""}`);
    const input = el("input");
    input.type = "radio";
    input.name = name;
    input.value = value;
    input.checked = checked;
    input.addEventListener("change", onPick);
    const text = el("span");
    text.append(el("span", "pk-choice-title", label), el("span", "pk-choice-hint", hint));
    l.append(input, text);
    return l;
  }

  function meterRow(key: "mic" | "call", name: string, sub: string, ok: boolean, okText: string, waitText: string): HTMLElement {
    const row = el("div", "pk-meter-row");
    const who = el("div", "pk-meter-who");
    who.append(el("span", undefined, name), el("span", "pk-meter-sub", sub));
    const meter = el("div", "pk-meter");
    meter.dataset.meter = key;
    meter.setAttribute("role", "meter");
    // E's finding 8 (axe aria-required-attr): a meter needs a value from the start ("You level" read oddly: "Your level").
    meter.setAttribute("aria-label", key === "mic" ? "Your level" : `${name} level`);
    meter.setAttribute("aria-valuemin", "0");
    meter.setAttribute("aria-valuemax", "100");
    meter.setAttribute("aria-valuenow", "0");
    meter.append(el("div"));
    row.append(who, meter, el("span", ok ? "pk-meter-ok" : "pk-meter-wait", ok ? okText : waitText));
    return row;
  }

  /** CAP-010 (approved design, page 3): real radio inputs in a fieldset (the browser gives arrow keys and one tab stop), shown as cards
   * with the client's initials, name and "Xero connected"; the chosen card has an Ink border, Coral Light and a filled tick (never
   * colour alone). The cards stay on screen; under them, "Recording for <client>" with Change, which takes focus back to the choice. */
  function clientChooser(): HTMLElement {
    const box = el("div", "pk5-client-choice");
    const fs = el("fieldset", "pk5-client-cards");
    fs.append(el("legend", "pk5-client-cards-legend", T.clientQuestion));
    for (const c of opts.clients) {
      const input = el("input", "pk5-client-radio");
      input.type = "radio";
      input.name = "pk-rec-client";
      input.value = c.client_slug;
      input.checked = c.client_slug === state.clientSlug;
      input.addEventListener("change", () => {
        state.clientSlug = c.client_slug;
        opts.onClientChange?.(c.client_slug);
        draw();
        opts.root.querySelector<HTMLInputElement>('input[name="pk-rec-client"]:checked')?.focus();
      });
      const ini = el("span", "pk5-client-card-ini", clientInitials(c.client_name));
      ini.setAttribute("aria-hidden", "true");
      const text = el("span", "pk5-client-card-text");
      // "Xero connected": every client offered here is one linked through Xero (/api/me's entities).
      text.append(el("span", "pk5-client-card-name", c.client_name), el("span", "pk5-client-card-sub", T.xeroConnected));
      const tick = el("span", "pk5-client-card-tick", "✓");
      tick.setAttribute("aria-hidden", "true");
      const card = el("label", "pk5-client-card");
      card.append(input, ini, text, tick);
      fs.append(card);
    }
    box.append(fs);
    const chosen = opts.clients.find((c) => c.client_slug === state.clientSlug);
    if (chosen) {
      const line = el("p", "pk5-recording-for");
      line.append(el("span", "pk5-recording-for-label", `${T.recordingFor} `), el("strong", "pk5-recording-for-client", chosen.client_name));
      if (opts.clients.length > 1) {
        line.append(
          button(T.change, "pk5-link-btn pk5-recording-change", () => {
            opts.root.querySelector<HTMLInputElement>('input[name="pk-rec-client"]:checked')?.focus();
          })
        );
      }
      box.append(line);
    }
    return box;
  }

  function draw(): void {
    if (destroyed || handedOver) return;
    const online = state.mode === "online";
    const page = el("div", "pk-record");
    const main = el("section", "pk-record-main");
    // The page header already reads "Record a meeting" (render.ts's h1).
    main.append(el("p", "muted pk-record-lead", T.lead));
    if (session === "signed_out") {
      // #303: nothing to check or tick until the person has signed in again; Sign in comes back here, same client.
      const box = el("div", "pk-alert pk-record-signed-out");
      box.setAttribute("role", "alert");
      box.dataset.pkSignedOut = "";
      const signIn = button(SIGNED_OUT.action, "btn btn-primary", () => opts.onSignIn?.(state.clientSlug || null));
      box.append(el("p", undefined, SIGNED_OUT.message), signIn);
      main.append(box);
      page.append(main);
      opts.root.replaceChildren(page);
      signIn.focus();
      return;
    }
    const cap = limitLine(opts.recordingLimitMinutes);
    if (cap) {
      const line = el("p", "pk-record-limit", cap);
      line.dataset.pkRecordingLimit = "notice";
      main.append(line);
    }

    // Client
    if (opts.clientCards) main.append(clientChooser());
    else {
    const cf = el("div", "pk-field");
    const cl = el("label", undefined, T.client);
    cl.htmlFor = "pk-rec-client";
    const sel = el("select", "pk-input");
    sel.id = "pk-rec-client";
    if (opts.clients.length !== 1) sel.append(new Option(T.chooseClient, ""));
    for (const c of opts.clients) sel.append(new Option(c.client_name, c.client_slug));
    sel.value = state.clientSlug;
    sel.addEventListener("change", () => {
      state.clientSlug = sel.value;
      draw();
    });
    cf.append(cl, sel);
    main.append(cf);
    }

    // Where
    const where = el("fieldset", "pk-choices");
    where.append(el("legend", undefined, T.where));
    const wg = el("div", "pk-choice-grid");
    const setMode = (m: RecordMode) => () => {
      if (m !== state.mode && mic) {
        // The microphone was opened with the other mode's processing (micConstraints); open it again for this one.
        stopStream(mic);
        mic = null;
        analysers.mic = null;
        state.mic = "not_run";
      }
      state.mode = m;
      if (m === "in_person") {
        stopStream(call);
        call = null;
        analysers.call = null;
        state.call = "not_run";
        state.surface = null;
        state.echo = "not_run";
      }
      draw();
    };
    wg.append(
      radio("pk-where", "online", online, T.online, T.onlineHint, setMode("online")),
      radio("pk-where", "in_person", !online, T.inPerson, T.inPersonHint, setMode("in_person"))
    );
    where.append(wg);
    main.append(where);

    // How Preekie hears the call
    if (online) {
      const src = el("fieldset", "pk-choices");
      src.append(el("legend", undefined, T.callSource));
      const sg = el("div", "pk-choice-grid");
      const setSource = (s: CallSource) => () => {
        state.source = s;
        stopStream(call);
        call = null;
        analysers.call = null;
        state.call = "not_run";
        state.surface = null;
        state.echo = "not_run";
        routeNote = null;
        draw();
      };
      sg.append(
        radio("pk-source", "tab", state.source === "tab", T.tab, T.tabHint, setSource("tab")),
        radio("pk-source", "screen", state.source === "screen", T.screen, T.screenHint, setSource("screen"))
      );
      src.append(sg);
      if (routeNote) {
        const n = el("p", "pk-notice", routeNote);
        n.setAttribute("role", "status");
        src.append(n);
      }
      // Said before the share picker, every time, on this route (session D).
      if (state.source === "screen") {
        const n = el("p", "pk-entire-screen-notice", ENTIRE_SCREEN_NOTICE);
        n.setAttribute("role", "note");
        src.append(n);
      }
      main.append(src);
    }

    // Sound check
    const sc = el("section", "pk-sound-check");
    sc.setAttribute("aria-labelledby", "pk-sc-h");
    const sch = el("h2", "pk-record-h2", T.soundCheck);
    sch.id = "pk-sc-h";
    sc.append(sch);
    sc.append(meterRow("mic", T.you, "Your microphone", state.mic === "ok", T.hearingYou, T.notHearingYou));
    if (online) sc.append(meterRow("call", T.callSound, "Everyone else on the call", state.call === "ok", T.hearingCall, T.notHearingCall));
    if (online && state.call === "ended") {
      const w = el("div", "pk-alert");
      w.setAttribute("role", "alert");
      w.append(el("span", undefined, `${T.sharingStopped} `), button(T.shareAgain, "btn btn-outline", () => void shareAgain()));
      sc.append(w);
    }
    const actions = el("div", "pk-actions");
    const check = button(state.mic === "not_run" ? T.checkSound : T.checkAgain, "btn btn-outline", () => void checkSound());
    check.disabled = session !== "ok";
    actions.append(check);
    if (online && state.call === "ok") {
      const e = button(echoUntil ? T.echoRunning : T.echoTest, "btn btn-outline", runEcho);
      e.disabled = !!echoUntil;
      actions.append(e);
    }
    sc.append(actions);
    if (online && state.echo !== "not_run") {
      sc.append(el("p", state.echo === "detected" ? "pk-echo pk-echo-heard" : "pk-echo", state.echo === "none" ? T.echoNone : T.echoHeard));
    } else if (online && state.call === "ok" && !echoUntil && state.echo === "not_run" && samples.length && state.source === "screen") {
      sc.append(el("p", "muted pk-echo", "Wear headphones and tick below. The echo check is a bonus: it can't replace the tick."));
    }
    if (online && state.source === "screen") {
      const hp = el("label", "pk-check");
      const box = el("input");
      box.type = "checkbox";
      box.checked = state.headphones;
      box.addEventListener("change", () => {
        state.headphones = box.checked;
        draw();
      });
      hp.append(box, el("span", undefined, T.headphones));
      sc.append(hp, el("p", "muted pk-field-hint", T.headphonesWhy));
    } else if (online && state.echo === "detected") {
      sc.append(el("p", "muted pk-field-hint", "Headphones are recommended for this call."));
    }
    main.append(sc);

    // Consent
    const cons = el("section", "pk-consent");
    cons.setAttribute("aria-labelledby", "pk-cons-h");
    const ch = el("h2", "pk-record-h2", T.beforeYouStart);
    ch.id = "pk-cons-h";
    const tick_ = el("label", "pk-check");
    const cbox = el("input");
    cbox.type = "checkbox";
    cbox.checked = state.consent;
    cbox.addEventListener("change", () => {
      state.consent = cbox.checked;
      draw();
    });
    tick_.append(cbox, el("span", undefined, T.consent));
    cons.append(ch, el("p", "muted pk-field-hint", T.showOrRead), el("blockquote", "pk-notice-quote pk-record-notice", consentShown.notice), tick_);
    // The calendar notice, to paste into the invite (same text on every route and screen size).
    const cal = el("div", "pk-calendar-notice");
    cal.append(el("h3", "pk-calendar-h", T.calendarHeading), el("p", "muted pk-field-hint", T.calendarHint));
    const calText = el("blockquote", "pk-notice-quote", T.calendarNotice);
    const copyRow = el("div", "pk-copy-row");
    const copied = el("span", "muted pk-copy-status");
    copied.setAttribute("role", "status");
    const copyBtn = button(T.copy, "btn btn-outline pk-btn-small", () => {
      if (!navigator.clipboard) {
        copied.textContent = T.copyFailed;
        return;
      }
      navigator.clipboard.writeText(T.calendarNotice).then(
        () => (copied.textContent = T.copied),
        () => (copied.textContent = T.copyFailed)
      );
    });
    copyBtn.setAttribute("aria-label", "Copy the calendar notice");
    copyRow.append(copyBtn, copied);
    cal.append(calText, copyRow);
    cons.append(cal);
    main.append(cons);

    if (error) {
      const a = el("div", "pk-alert", error);
      a.setAttribute("role", "alert");
      main.append(a);
    }
    for (const o of openElsewhere) {
      const c = openElsewhereCopy(o.started_at, opts.timeZone);
      const box = el("div", "pk-unsent pk-unsent-warn");
      box.setAttribute("role", "alert");
      box.append(el("span", "pk-unsent-text", c.text), button(c.discard, "btn btn-outline pk-btn-small", () => discardElsewhere(o.recording_id, o.started_at)));
      main.append(box);
    }
    const blockers = startBlockers(state);
    if (!opts.recordingAvailable) blockers.push(NOT_YET_AVAILABLE);
    if (openElsewhere.length) blockers.push("Finish or discard the open recording first.");
    if (session === "checking") blockers.push(CHECKING_SESSION);
    const go = button(starting ? "Starting…" : T.start, "btn btn-primary pk-record-start", () => void start());
    go.disabled = starting || blockers.length > 0;
    if (blockers.length) go.setAttribute("aria-describedby", "pk-rec-why");
    main.append(go);
    if (blockers.length) {
      const why = el("ul", "pk-record-why");
      why.id = "pk-rec-why";
      why.setAttribute("aria-live", "polite");
      for (const b of blockers) why.append(el("li", undefined, b));
      main.append(why);
    }

    const aside = el("aside", "pk-record-aside");
    aside.setAttribute("aria-labelledby", "pk-next-h");
    const ah = el("h2", "pk-record-h2", T.next);
    ah.id = "pk-next-h";
    const ol = el("ol");
    for (const s of T.nextSteps) ol.append(el("li", undefined, s));
    aside.append(ah, ol, el("p", "muted pk-record-deleted", T.deleted));

    page.append(main, aside);
    // Keep focus on the same control across redraws (choices, ticks, buttons).
    const focused = document.activeElement as HTMLElement | null;
    const key = focused && opts.root.contains(focused) ? focused.id || (focused as HTMLInputElement).value || focused.textContent : null;
    opts.root.replaceChildren(page);
    if (key) {
      const again = Array.from(opts.root.querySelectorAll<HTMLElement>("input, select, button")).find(
        (n) => n.id === key || (n as HTMLInputElement).value === key || n.textContent === key
      );
      again?.focus();
    }
  }

  draw();
  if (opts.checkSession) {
    void sessionState(opts.checkSession).then((s) => {
      if (destroyed) return;
      if (s === "signed_out") return signedOut();
      session = "ok";
      draw();
      void loadOpenElsewhere();
    });
  } else void loadOpenElsewhere();
  return {
    destroy() {
      destroyed = true;
      window.clearInterval(timer);
      if (!handedOver) {
        stopStream(mic);
        stopStream(call);
        void ctx?.close();
      }
    },
  };
}
